import {Autocomplete} from "@mui/material";
import * as React from 'react';
import {SyntheticEvent} from 'react';
import Box from '@mui/material/Box';
import {TextField} from "formik-mui";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import {Field, useFormikContext} from "formik";
import {Services} from "./GoogleMapsServices";

//FIXME (mmilburn): not sure why I can't "import" the types as webstorm suggests.
type AutocompletionRequest = google.maps.places.AutocompletionRequest;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type GeocoderResult = google.maps.GeocoderResult;
type GeocoderStatus = google.maps.GeocoderStatus;
type PlacesServiceStatus = google.maps.places.PlacesServiceStatus;

interface Deps {
    prefix: string;
    mapsServices: Services;
}

interface AddressFields {
    streetNumber: string,
    street: string,
    city: string,
    state: string,
    zip: string
}

enum Precision {
    LOW,
    HIGH
}

interface GeoCoords {
    latitude: number,
    longitude: number,
    precision: Precision,
}

/**
 * This AddressAutocomplete component currently functions as a block of formik fields.
 * @param props - The namespace all submitted values will belong to.
 * @constructor
 */
function AddressAutocomplete(props: Deps) {
    const [value, setValue] = React.useState<AutocompletePrediction | null>(null);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<readonly AutocompletePrediction[]>([]);
    const {setFieldValue} = useFormikContext();
    const {autocomplete, geocoder} = props.mapsServices;
    //Remove any trailing '.'
    const namespacePrefix = props.prefix.trim().replace(/\.+$/, "");
    const prefixedName = (name: string) => ([namespacePrefix, name].join("."));

    //Throttle (debounce) requests to the Places Autocomplete API to prevent making calls for every keypress.
    //Additionally, memoize any results in case the user looks the same thing up again.
    const fetch = React.useMemo(
        () =>
            throttle(
                (request: AutocompletionRequest,
                 callback: (results: readonly AutocompletePrediction[] | null, status: PlacesServiceStatus) => void,
                ) => {
                    if (autocomplete.current) {
                        autocomplete.current.getPlacePredictions(request, callback);
                    } else {
                        console.warn("Autocomplete service not loaded!")
                    }
                },
                350,
            ),
        [autocomplete],
    );

    React.useEffect(() => {
        let active = true;

        if (!autocomplete.current) {
            return undefined;
        }
        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        if (inputValue && inputValue.length > 2) {
            fetch({
                input: inputValue,
                componentRestrictions: {country: "US"},
                //FIXME (mmilburn): Add autocomplete session token. Ideally it should only change on component load.
                types: ["address"],
            }, (results: readonly AutocompletePrediction[] | null, status: PlacesServiceStatus) => {
                if (active) {
                    let newOptions: readonly AutocompletePrediction[] = [];
                    if (value) {
                        newOptions = [value];
                    }
                    if (results) {
                        newOptions = [...newOptions, ...results];
                    }
                    setOptions(newOptions);
                }
                //FIXME (mmilburn): How can I refer to these enum values in a terse manner?
                if (status !== google.maps.places.PlacesServiceStatus.OK &&
                    status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                    alert("Places API error: " + status);
                }
            });
        }

        return () => {
            active = false;
        };
    }, [autocomplete, fetch, inputValue, value]);

    const setAddressFields = (address: AddressFields, coords: GeoCoords) => {
        //zip your parameters into tuples and you could have yourself a map-able call to setFieldValue().
        //https://www.freecodecamp.org/news/the-zip-function-in-python-explained-with-examples/
        setFieldValue(prefixedName("street1"), [address.streetNumber, address.street].join(" "));
        setFieldValue(prefixedName("city"), address.city);
        setFieldValue(prefixedName("state"), address.state);
        setFieldValue(prefixedName("zip"), address.zip);
        setFieldValue(prefixedName("coords.latlng"), [coords.latitude, coords.longitude].join(","));
        setFieldValue(prefixedName("coords.precision"), coords.precision);
    };

    const clearAddressFields = () => {
        ["street1", "city", "state", "zip", "coords.latlng", "coords.precision"].map((name) => (
            setFieldValue(prefixedName(name), "")
        ));
    }

    const extractAddress = (result: GeocoderResult): AddressFields => {
        /*"The Geocoding API isn't guaranteed to return any particular component for an address within our data set.
        What may be thought of as the city, such as Brooklyn, may not show up as locality, but rather as another
        component - in this case, sublocality_level_1. What specific components are returned is subject to change
        without notice. Design your code to be flexible if you are attempting to extract address components from the
        response."
        Uhh, great.*/
        //TODO (mmilburn): rework imperative mutable stuff.
        let acc: AddressFields = {
            streetNumber: "",
            street: "",
            city: "",
            state: "",
            zip: ""
        };
        for (let i = 0; i < result.address_components.length; i++) {
            const component = result.address_components[i];
            for (let j = 0; j < component.types.length; j++) {
                const type = component.types[j];
                //https://hackernoon.com/rethinking-javascript-eliminate-the-switch-statement-for-better-code-5c81c044716d
                switch (type) {
                    case "street_number":
                        acc.streetNumber = component.long_name.trim();
                        break;
                    case "route":
                        //I want abbreviated street suffixes, thanks.
                        acc.street = component.short_name.trim();
                        break;
                    case "locality":
                        acc.city = component.long_name.trim();
                        break;
                        //FIXME (mmilburn): Sometimes we can get "Brooklyn" instead of "NYC". Gotta sort out a way to
                        //handle this gracefully.
                    case "sublocality_level_1":
                        acc.city = component.long_name.trim();
                        break;
                    //state
                    case "administrative_area_level_1":
                        acc.state = component.short_name.trim();
                        break;
                    case "postal_code":
                        acc.zip = component.long_name.trim();
                        break;
                    default:
                        //console.debug("Unhandled address_component type: " + type);
                        break;
                }
            }
        }
        return acc;
    }

    const extractLatLng = (result: GeocoderResult): GeoCoords => {
        return {
            latitude: result.geometry.location.lat(),
            longitude: result.geometry.location.lng(),
            precision: result.geometry.location_type === google.maps.GeocoderLocationType.ROOFTOP ? Precision.HIGH : Precision.LOW,
        };
    };

    const geoCallback = (results: GeocoderResult[] | null, status: GeocoderStatus) => {
        if (status !== google.maps.GeocoderStatus.OK) {
            alert("Geocoder status error: " + status)
        }

        if (results) {
            if (results.length > 1) {
                console.warn("Multiple Geocoder results returned.");
            }
            const address: AddressFields = extractAddress(results[0]);
            const coords: GeoCoords = extractLatLng(results[0]);
            setAddressFields(address, coords);
        }
    };

    const handleOnChange = (event: SyntheticEvent, newValue: AutocompletePrediction | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue) {
            if (geocoder.current) {
                geocoder.current.geocode({placeId: newValue.place_id}, geoCallback);
            }
            //geocoder.current was null
            else {
                console.warn("Geocoder service not loaded!");
            }
        } else {
            clearAddressFields();
        }
    };

    return (<>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="google-autocomplete"
                    getOptionLabel={(option) =>
                        option.description
                    }
                    //Avoid predictions that don't actually give us addresses, despite our already requesting only
                    // addresses with AutocompleteRequest's types parameter.
                    filterOptions={(x: AutocompletePrediction[]) => {
                        /*"The street_number address component may not be present in Place Details responses for
                        place IDs in some autocomplete predictions. This typically happens when the address cannot be
                        interpolated for a place. In these cases, the prediction's types array will contain "route"."*/
                        return x.filter((pred: AutocompletePrediction) => {
                            return !pred.types.find(elt => elt === "route")
                        });
                    }}
                    options={options}
                    autoComplete
                    filterSelectedOptions
                    value={value}
                    onChange={handleOnChange}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    renderInput={(params) =>
                        (<Field
                            {...params}
                            name={prefixedName("searchBox")}
                            label="Address Search"
                            fullWidth={true}
                                component={TextField}/>
                        )
                    }
                    //This highlights partial matches in the options.
                    renderOption={(props, option) => {
                        const matches = option.structured_formatting.main_text_matched_substrings;
                        const parts = parse(
                            option.structured_formatting.main_text,
                            matches.map((match: any) => [match.offset, match.offset + match.length]),
                        );
                        //FIXME (mmilburn): We get a "each child in list should have a unique key" warning *only* on
                        //first keypress after the initial load of the component.
                        return (
                            <li {...props}>
                                <Grid container alignItems="center">
                                    <Grid item>
                                        <Box
                                            component={LocationOnIcon}
                                            sx={{color: 'text.secondary', mr: 2}}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        {parts.map((part, index) => {
                                            return (
                                            <span
                                                key={index}
                                                style={{
                                                    fontWeight: part.highlight ? 700 : 400,
                                                }}
                                            >
                                                    {part.text}
                                            </span>
                                            );
                                        })}
                                        <Typography variant="body2" color="text.secondary">
                                            {option.structured_formatting.secondary_text}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </li>
                        );
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                {/*Coords must be cleared if editing is enabled for any address fields aside from street 2. If this
                    is not done, the wrong lat long will be saved if the address has been changed.*/}
                <Field
                    name={prefixedName("street1")}
                    label={"Street Address"}
                    component={TextField}
                    fullWidth={true}
                    inputProps={{readOnly: true}}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Field
                    name={prefixedName("street2")}
                    label={"Unit Letter/Number"}
                    fullWidth={true}
                    component={TextField}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Field
                    name={prefixedName("city")}
                    label={"City"}
                    fullWidth={true}
                    component={TextField}
                    inputProps={{readOnly: true}}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Field
                    name={prefixedName("state")}
                    label={"State"}
                    fullWidth={true}
                    component={TextField}
                    inputProps={{readOnly: true}}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Field
                    name={prefixedName("zip")}
                    label={"Zip Code"}
                    fullWidth={true}
                    component={TextField}
                    inputProps={{readOnly: true}}
                />
            </Grid>
        </>
    );
}

export {AddressAutocomplete};